<template>
    <div
        class="ask-pendo-context-button"
        :class="{ 'ask-pendo-context-button--icon-only': iconOnly }">
        <pendo-button
            v-if="!iconOnly"
            theme="app"
            type="tertiary"
            :disabled="disabled"
            pendo-feature="ask-pendo-context-button"
            @click="openConversationDrawer">
            <leo-sparkle-icon class="ask-pendo-context-button__trigger-icon" />
            {{ $t('askPendo.Ask agent mode') }}
        </pendo-button>
        <pendo-button
            v-else
            v-pendo-tooltip="$t('askPendo.Ask agent mode')"
            class="ask-pendo-context-button__icon-button"
            theme="app"
            type="tertiary"
            :disabled="disabled"
            pendo-feature="ask-pendo-context-button"
            @mouseenter="isIconHovered = true"
            @mouseleave="isIconHovered = false"
            @click="openConversationDrawer">
            <leo-sparkle-icon
                class="ask-pendo-context-button__trigger-icon"
                :spinning="isIconHovered" />
        </pendo-button>

        <pendo-drawer
            :visible="isDrawerOpen"
            :size="drawerSize"
            push=".vue-wrapper"
            @close="closeConversationDrawer">
            <template #header>
                <div class="ask-pendo-context-button__header">
                    <div class="ask-pendo-context-button__title">
                        <leo-sparkle-icon class="ask-pendo-context-button__title-icon" />
                        <span class="ask-pendo-context-button__title-text">{{ $t('askPendo.Agent mode') }}</span>
                        <pendo-tag
                            size="small"
                            type="beta" />
                    </div>
                    <div class="ask-pendo-context-button__header-actions">
                        <pendo-icon-button
                            icon="edit"
                            icon-size="16"
                            type="tertiary"
                            :tooltip="$t('askPendo.New conversation')"
                            @click="startNewConversation" />
                        <ask-pendo-saved-chat icon-only />
                        <pendo-icon-button
                            icon="maximize-2"
                            icon-size="16"
                            type="tertiary"
                            :tooltip="$t('askPendo.Expand')"
                            @click="expandToFullScreen" />
                        <span v-pendo-tooltip="$t('askPendo.Close chat')">
                            <pendo-drawer-close />
                        </span>
                    </div>
                </div>
            </template>
            <template #body>
                <div
                    ref="drawerBody"
                    class="ask-pendo-context-button__body"
                    tabindex="-1">
                    <ask-pendo-conversation
                        v-if="conversationId"
                        :id="conversationId"
                        :key="conversationId"
                        hide-header
                        @artifact-pane-change="onArtifactPaneChange" />
                </div>
            </template>
        </pendo-drawer>
    </div>
</template>

<script>
import { PendoButton, PendoIconButton, PendoDrawer, PendoDrawerClose, PendoTag, PendoTooltip } from '@pendo/components';
import { mapActions } from 'vuex';
import { v4 as uuid } from 'uuid';
import AskPendoConversation from '@/ask-pendo/ask-pendo-conversation.vue';
import AskPendoSavedChat from '@/ask-pendo/components/ask-pendo-saved-chat.vue';
import LeoSparkleIcon from '@/ask-pendo/components/leo-sparkle-icon.vue';
import { getInContextPrompt } from '../utils/in-context-prompt-utils';

export default {
    name: 'AskPendoContextButton',
    components: {
        AskPendoConversation,
        AskPendoSavedChat,
        LeoSparkleIcon,
        PendoButton,
        PendoIconButton,
        PendoDrawer,
        PendoDrawerClose,
        PendoTag,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        /**
         * Metadata describing the context in which the conversation is taking place.
         */
        context: {
            type: Object,
            default: () => ({}),
        },
        countableType: {
            type: String,
            required: true,
        },
        countableId: {
            type: String,
            required: true,
        },
        countableName: {
            type: String,
            required: true,
        },
        suggestedQuestions: {
            type: Array,
            default: () => [],
        },
        iconOnly: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        /** When provided, called on click to get conversation id instead of creating with prompt. Enables custom flows (e.g. table artifact). */
        getConversationId: {
            type: Function,
            default: null,
        },
    },
    emits: ['open-conversation-drawer', 'close-conversation-drawer'],
    data () {
        return {
            isDrawerOpen: false,
            conversationId: null,
            drawerSize: '448px',
            isIconHovered: false,
        };
    },
    watch: {
        $route () {
            if (this.isDrawerOpen) {
                this.isDrawerOpen = false;
                this.$emit('close-conversation-drawer');
            }
            this.conversationId = null;
            this.drawerSize = '448px';
        },
    },
    methods: {
        ...mapActions('askPendo', ['createConversation']),
        async openConversationDrawer () {
            this.$emit('open-conversation-drawer');
            let conversationId;
            if (this.getConversationId) {
                conversationId = await this.getConversationId();
            } else {
                const prompt = this.generatePrompt();
                conversationId = uuid();
                const artifacts = {};
                for (const question of this.suggestedQuestions) {
                    artifacts[uuid()] = {
                        type: 'next_actions',
                        action_type: 'ask_question',
                        data: {
                            question,
                            prompt,
                        },
                    };
                }
                const metadata = {
                    heading_type: 'ask_question',
                    artifact_ids: Object.keys(artifacts),
                };
                await this.createConversation({
                    id: conversationId,
                    prompt,
                    metadata,
                    artifacts,
                    user_context: this.countableName
                        ? { type: 'countable', displayName: this.countableName }
                        : this.context,
                });
            }

            if (conversationId) {
                this.conversationId = conversationId;
                this.isDrawerOpen = true;
                await this.$nextTick();
                this.$refs.drawerBody?.focus();
            }
        },
        generatePrompt () {
            return getInContextPrompt(this.countableType, this.countableId, this.countableName);
        },
        closeConversationDrawer () {
            this.isDrawerOpen = false;
            this.drawerSize = '448px';
            this.$emit('close-conversation-drawer');
        },
        startNewConversation () {
            this.closeConversationDrawer();
            this.$router.push({ name: 'AgentMode' });
        },
        expandToFullScreen () {
            this.closeConversationDrawer();
            this.$router.push({ name: 'AgentModeConversation', params: { id: this.conversationId } });
        },
        onArtifactPaneChange (artifactPaneVisible) {
            this.drawerSize = artifactPaneVisible ? '720px' : '448px';
        },
    },
};
</script>

<style scoped lang="less">
.ask-pendo-context-button {
    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        position: relative;
    }

    &__trigger-icon {
        width: 24px;
        height: 24px;
        flex-shrink: 0;
    }

    &__title {
        display: flex;
        align-items: center;
        gap: 4px;
    }

    &__title-icon {
        width: 32px;
        height: 32px;
        flex-shrink: 0;
        margin-left: 2px;
    }

    &__title-text {
        font-weight: 600;
        font-size: 18px;
        margin-left: 2px;
    }

    &__header-actions {
        display: flex;
        align-items: center;
        gap: 8px;

        :deep(.pendo-icon-button),
        :deep(.saved-chat-trigger--icon-only),
        :deep(.pendo-drawer__close) {
            width: auto;
            height: auto;
            min-width: unset;
            padding: 4px 8px;
            box-sizing: border-box;
            color: var(--palette-gray-100);
            background-color: transparent;
            border-color: transparent;

            svg {
                color: var(--palette-gray-100);
                stroke: var(--palette-gray-100);
            }

            &:hover,
            &.is-open,
            &:focus {
                background-color: transparent;
                border-color: transparent;
                box-shadow: none;
            }
        }
    }

    &__body {
        position: relative;
        height: 100%;
        min-height: 640px;
    }

    &--icon-only :deep(.ask-pendo-context-button__icon-button) {
        width: 32px;
        height: 32px;
        min-width: 32px;
        padding: 0;

        span {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
        }

        &:hover:not(.is-disabled) .ask-pendo-context-button__trigger-icon {
            filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.25));
        }
    }
}

:deep(.pendo-drawer__body) {
    padding: 0;
    overflow: hidden;
}
</style>
